import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { userAtom } from "../../recoil/userAtom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import FullPageWithHeaderAndProgressV2 from "../container/FullPageWithHeaderAndProgressV2";
import RefundSurveySubmit from "./RefundSurveySubmit";
import RefundRequest from "./RefundRequest";
import CustomModal from "../../components/common/CustomModal";
import useIsInappWebview from "../../hooks/useIsInappWebview";
import { sendMessage } from "../../utils/webview";
import { pcSideTabAtom } from "../../recoil/pcSideTabAtom";
import useMixpanelWeb from "../../hooks/useMixpanelWeb";
import { useQuery } from "@tanstack/react-query";
import { decideInitialRefund } from "../../utils/refundPolicyUtils";
import {
  COMMITMENT_DETAIL_ENDPOINT,
  COMMITMENT_DOMAIN,
  commitmentApi,
} from "../../api/commitmentApi";
import RefundAmount from "./RefundAmount";

interface RefundStep {
  seq: number;
  title?: string;
  hideHeader?: boolean;
  hideProgress?: boolean;
  disableBack?: boolean;
  progressDelta?: number;
  highlightBackground?: boolean;
  content: React.ReactNode;
  hideClose?: boolean;
}

const refundTypeCSteps: RefundStep[] = [
  {
    seq: 0,
    title: "환불 신청",
    hideProgress: true,
    hideClose: true,
    content: (
      <RefundSurveySubmit
        pageSeq={0}
        questionId={0}
        contentTextList={["어떤 이유로 환불을 결심하게 되셨나요?"]}
        placeholder={"자세하게 적어주세요."}
      />
    ),
  },
  {
    seq: 1,
    title: "환불 신청",
    hideProgress: true,
    hideClose: true,
    content: <RefundAmount pageSeq={1} />,
  },
  {
    seq: 2,
    title: "환불 신청",
    hideProgress: true,
    hideClose: true,
    content: <RefundRequest />,
  },
];

function findIndexByStepInfo(steps: RefundStep[], seq: number): number {
  return steps.findIndex((step) => step.seq === seq);
}

export default function RefundScreen() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useRecoilValue(userAtom);
  const isInappWebview = useIsInappWebview();
  const setPCSideTab = useSetRecoilState(pcSideTabAtom);

  const [showNoPaymentModal, setShowNoPaymentModal] = useState(false);
  const [entryModalTitle, setEntryModalTitle] =
    useState<string>("환불할 결제가 없어요");
  const [entryModalSubtitle, setEntryModalSubtitle] = useState<string>(
    "현재 환불을 신청할 결제 내역이 존재하지 않아요."
  );

  useEffect(() => {
    if (!user) return;
    const decision = decideInitialRefund({ user: user, limitDays: 30 });
    if (decision.key !== "EMAIL") {
      setEntryModalTitle(decision.title);
      setEntryModalSubtitle(decision.subtitle);
      setShowNoPaymentModal(true);
    } else {
      setShowNoPaymentModal(false);
    }
  }, [user]);

  const handleExit = () => {
    if (isInappWebview) {
      sendMessage(
        JSON.stringify({
          event: "navigate_back",
        })
      );
    } else {
      navigate("/", { replace: true });
    }
  };

  const seq = Number.parseInt(searchParams.get("seq") || "0");

  const currentStepIndex = Math.min(
    Math.max(findIndexByStepInfo(refundTypeCSteps, seq), 0),
    refundTypeCSteps.length - 1
  ); // 유효한 범위 내로 제한

  const currentStep = refundTypeCSteps[currentStepIndex];

  // Fetch commitment detail to determine if signature exists, used for back-navigation skip logic
  const { data: commitmentDetail, isLoading: isLoadingCommit } = useQuery(
    [COMMITMENT_DOMAIN, COMMITMENT_DETAIL_ENDPOINT, user?.userId],
    () => commitmentApi.getDetail(),
    {
      staleTime: 1000 * 60,
    }
  );

  const rawSig = (commitmentDetail as any)?.result?.signature as
    | string
    | undefined
    | null;
  const hasSignature = !!rawSig;

  const handleBack = () => {
    // Determine the previous navigable step, skipping signature refundTypeCSteps when signature is absent
    let targetIndex = currentStepIndex - 1;

    const shouldSkipSignature = !isLoadingCommit && !hasSignature;

    while (targetIndex >= 0) {
      const step = refundTypeCSteps[targetIndex];
      const isSignatureStep = step.seq === 14;
      if (isSignatureStep && shouldSkipSignature) {
        targetIndex -= 1;
        continue;
      }
      break;
    }

    if (targetIndex < 0) {
      if (isInappWebview) {
        sendMessage(
          JSON.stringify({
            event: "navigate_back",
          })
        );
      } else {
        navigate("/", { replace: true });
        // setPCSideTab("mypage")
        // navigate(`/support-faq?fid=101`, { replace: true });
      }
      return;
    }

    const prevStep = refundTypeCSteps[targetIndex];
    navigate(`/refund?seq=${prevStep.seq}`);
  };

  // const setMixPanel = useMixpanelWeb({
  //   eventName: "환불 페이지",
  //   params: {
  //     type: `${currentStep.type}`,
  //     pageId: `${currentStep.seq}`,
  //   },
  // });
  //
  // useEffect(() => {
  //   setMixPanel();
  // }, [id]);

  const setMixPanel = useMixpanelWeb({
    eventName: "환불 신청",
    params: {
      pageId: `${seq}`,
    },
  });

  useEffect(() => {
    setMixPanel();
  }, [seq]);

  if (showNoPaymentModal) {
    return (
      <CustomModal
        open={showNoPaymentModal}
        onClose={handleExit}
        title={entryModalTitle}
        subtitle={entryModalSubtitle}
        actions={[
          {
            label: "확인",
            onClick: handleExit,
            variant: "Solid",
          },
        ]}
      />
    );
  }

  return (
    <FullPageWithHeaderAndProgressV2
      headerTitle={currentStep.title}
      hideHeader={currentStep.hideHeader}
      hideProgress={currentStep.hideProgress}
      progress={9.1 * (currentStepIndex + 1)}
      body={currentStep.content}
      highlightBackground={currentStep.highlightBackground}
      disableBack={currentStep.disableBack}
      onBack={handleBack}
      showClose={!currentStep.hideClose}
    />
  );
}
